/**
 * A PDF of printed music sent to the server and read into a score: the sending, the starting
 * and the following that every board asking for a reading shares.
 *
 * The file is uploaded, a reading of it is started by the reader named (where none is, the
 * server's own reader where it has one, Audiveris otherwise), and the job is asked after until it ends. A reading takes tens of seconds a page, so
 * the caller is told each step and can stop waiting at any of them: `live` is asked after
 * every answer, and a job started for a page that has stopped waiting is stopped too. A caller
 * that shows what has been read before the whole is (`sofar`) is handed it as it grows.
 */

import { UPLOAD_LIMIT } from "@app/shared-types";
import type { RecognisePartial, RecognisePayload, RecogniseProgress } from "@app/shared-types";
import { api } from "../api/client";
import { bytes } from "./format";
import { followed } from "./job-poll";

/** How far a scan has got: being sent, or being read, without what has been read so far. */
export type ScanProgress = RecogniseProgress | { readonly stage: "uploading" };

/** Whether `data` is a PDF, by its first bytes rather than its name. */
export function isPdf(data: Uint8Array): boolean {
  return [0x25, 0x50, 0x44, 0x46].every((byte, at) => data[at] === byte);
}

/**
 * `file` read by `reader`; `null` when the page stopped waiting for it.
 *
 * `started` is told the job once there is one, so the caller can stop it; `sofar`, where
 * given, each reading so far newer than the last.
 */
export async function scanRead(
  file: File,
  reader: string | undefined,
  live: () => boolean,
  told: (progress: ScanProgress) => void,
  started: (job: string) => void,
  sofar?: (partial: RecognisePartial) => void,
): Promise<RecognisePayload | null> {
  if (file.size > UPLOAD_LIMIT) {
    throw new Error(`it is ${bytes(file.size)}; a scan may run to ${bytes(UPLOAD_LIMIT)}`);
  }
  told({ stage: "uploading" });
  const { upload } = await api.upload(file);
  if (!live()) return null;
  told({ stage: "scan" });
  const job = await api.recognise(upload, reader);
  if (!live()) {
    // Stopped while the job was being started: it exists now, and nobody will poll it.
    void api.cancelRecognise(job.job).catch(() => undefined);
    return null;
  }
  started(job.job);
  // Asked for only by a caller that shows it, and then only newer than the one it has.
  let since = 0;
  const ask = () => api.recogniseJob(job.job, sofar === undefined ? undefined : since);
  const heard = ({ partial, ...progress }: RecogniseProgress): void => {
    told(progress);
    if (partial === undefined || sofar === undefined) return;
    since = progress.partials ?? since;
    sofar(partial);
  };
  const read = await followed(ask, live, heard);
  if (read !== null || !live()) return read;
  // Nobody here stopped it: the server does, when a page has not asked for two minutes.
  throw new Error("the server stopped reading it, not having heard from this page");
}
